<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Conference Records" icon="fa-screen-users"/>
            <p class="mt-6 has-text-white" v-if="!registrations"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading your records...</p>
            <div v-else class="mt-6 content">
                <div class="columns">
                    <div class="column is-9-fullhd">
                        <div v-if="registrations.length === 0">
                            <p class="has-text-white"><i class="far fa-info-circle fa-fw mr-3"></i>You currently have no conference registrations.<br><i class="far fa-fw mr-3"></i><span class="has-text-weight-bold">Browse the available conferences in the Conference Center to get started.</span></p>
                        </div>
                        <div v-else>
                            <section class="mb-6 pb-2" v-if="recentRegistrations.length">
                                <h2 class="mb-6 has-text-white"><i class="fad fa-arrow-right fa-fw mr-3"></i>Recently Registered</h2>
                                <registration :registration="r" v-for="r in recentRegistrations" class="mb-5"/>

                            </section>
                            <section class="mb-6 pb-2" v-if="featuredConferences.length">
                                <h2 class="mb-6 has-text-white"><i class="fad fa-calendar-alt fa-fw mr-3"></i>Active & Recent Conferences</h2>
                                <registration :registration="r" v-for="r in featuredConferences" class="mb-5"/>

                            </section>
                            <section class="mb-6 pb-2" v-if="alertRegistrations.length">
                                <h2 class="mb-6 has-text-white"><i class="fad fa-exclamation-triangle fa-fw mr-3"></i>Records Requiring Attention</h2>
                                <registration :registration="r" v-for="r in alertRegistrations" class="mb-5"/>

                            </section>
                            <section class="mb-6 pb-2">
                                <h2 class="mb-4 has-text-white"><i class="fad fa-screen-users fa-fw mr-3"></i>All Conference Records</h2>

                                <!-- Year accordions (shared archive pattern with Course
                                     Enrollments / Reports); the current year opens by default -->
                                <archive-section v-for="year in recordYears" :key="'yr-' + year"
                                                 icon="fa-calendar" :title="year" :count="getByYear(year).length"
                                                 :open="year === defaultOpenYear">
                                    <registration :registration="r" v-for="r in getByYear(year)" :key="r.id" class="mb-5"/>
                                </archive-section>
                            </section>
                        </div>
                    </div>
                    <div class="column has-text-white has-text-right is-3-fullhd is-hidden-touch is-hidden-desktop-only is-hidden-widescreen-only">
                        <div class="mb-6">
                            <router-link :to="{name: 'conference-center'}">
                                <b-button type="is-light" outlined><i class="fad fa-browsers mr-3"></i>Browse Available Conferences</b-button>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </framework>
</template>

<script>
    import MainHeader from "../../components/framework/MainHeader";
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import {client as http} from "../../http_client";
    import Registration from "../../components/Registration";
    import ArchiveSection from "../../components/framework/ArchiveSection";
    import {mapActions, mapGetters} from "vuex";
    import moment from "moment/moment";
    import Conference from "../../components/Conference.vue";

    export default {
        components: {ArchiveSection, Conference, Registration, Framework, SidePanelNav, MainHeader},
        data: function() {
            return {
                showCompleted: false
            };
        },
        computed: {
            ...mapGetters(['registrations']),
            // years are sorted descending; open the current year, otherwise
            // the most recent past year, otherwise the oldest section
            defaultOpenYear: function() {
                const years = this.recordYears;
                if (!years.length) return null;
                const currentYear = new Date().getFullYear();
                return years.find(y => y <= currentYear) || years[years.length - 1];
            },
            recentRegistrations: function() {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        let created = moment(r.created_at);
                        return (created.isBetween(moment(), moment().clone().add(30, 'days')) ||
                            created.isBetween(moment().clone().subtract(14, 'days'), moment()));
                    });
                } else {
                    return [];
                }
            },
            alertRegistrations: function() {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        return r.isIncompleteApps;
                    });
                } else {
                    return [];
                }
            },
            featuredConferences: function() {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        let start = moment(r.conferences.start_at);
                        let end = moment(r.conferences.end_at);
                        return ((start.isBetween(moment(), moment().clone().add(30, 'days')) ||
                                    start.isBetween(moment().clone().subtract(14, 'days'), moment())) ||
                                (end.isBetween(moment(), moment().clone().add(30, 'days')) ||
                                    end.isBetween(moment().clone().subtract(14, 'days'), moment())))
                            && !this.recentRegistrations.includes(r);
                    });
                } else {
                    return [];
                }
            },
            recordYears: function() {
                if (this.registrations) {
                    const years = new Set();
                    this.registrations.forEach(r => {
                        const startYear = new Date(r.conferences.start_at).getFullYear();
                        const endYear = new Date(r.conferences.end_at).getFullYear();
                        for (let y = startYear; y <= endYear; y++) {
                            years.add(y);
                        }
                    });
                    return [...years].sort((a, b) => b - a);
                } else {
                    return [];
                }
            }
        },
        methods: {
            ...mapActions(['getRegistrations']),
            getByYear(year) {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        const startYear = new Date(r.conferences.start_at).getFullYear();
                        const endYear = new Date(r.conferences.end_at).getFullYear();
                        return year >= startYear && year <= endYear;
                    });
                } else {
                    return [];
                }
            }
        },
        mounted() {
            this.getRegistrations();
        }
    };
</script>

<style lang="scss" scoped>
    h2 {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }
</style>


